/**
 * FunnelConfig (TASK-238, extended TASK-301)
 *
 * Per-org funnel configuration:
 *   - editable list of funnel stages (label / order / is_active / mode /
 *     allowed_tools / goal). Each row saves via PATCH /funnel/stages/{id}/.
 *   - configurable next-step (follow-up) types (visit / callback / …) with CRUD
 *     via /funnel/next-step-types/.
 *   - event → stage mappings (target stage editable, mapping deletable) via
 *     /funnel/mappings/.
 *
 * Write controls are gated by `canManage` (users.manage). The parent renders
 * this component only for owners, so `canManage` defaults to true.
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { funnelApi } from '@/services/api/funnel';
import { LEAD_TEMPERATURE_LEVELS, LEAD_TEMPERATURE_LABELS } from '@/lib/leadTemperature';
import type {
  FunnelStageConfig,
  EventStageMapping,
  NextStepType,
  StageMode,
  StageMinTemperature,
  UpdateFunnelStagePayload,
} from '@/types/funnel';

/** Known tools that the bot can use. */
const KNOWN_TOOLS = [
  { value: 'compute_estimate', label: 'Расчёт сметы' },
  { value: 'search_info', label: 'Поиск информации' },
  { value: 'schedule_next_step', label: 'Запись следующего шага' },
  { value: 'end_call', label: 'Завершение звонка' },
] as const;

/** Stage mode options. */
const MODE_OPTIONS: { value: StageMode; label: string; hint: string }[] = [
  { value: '', label: 'По умолчанию', hint: 'Стандартное поведение бота' },
  { value: 'qualify', label: 'Квалификация', hint: 'Выявить потребности клиента' },
  { value: 'collect_estimate', label: 'Расчёт сметы', hint: 'Собрать параметры и рассчитать' },
  { value: 'close_deal', label: 'Закрытие сделки', hint: 'Дожать клиента до оплаты' },
  { value: 'support', label: 'Поддержка', hint: 'Ответить на вопросы' },
];

/** Editable local copy of a stage row. */
interface StageDraft {
  label: string;
  order: number;
  is_active: boolean;
  template_override: string;
  mode: StageMode;
  allowed_tools: string[] | null;
  goal: string;
  is_sale: boolean;
  min_temperature: StageMinTemperature;
}

/** KAN-966: Select value for "no temperature" (Radix Select forbids ''). */
const NO_TEMPERATURE = '_none';

function toDraft(stage: FunnelStageConfig): StageDraft {
  return {
    label: stage.label,
    order: stage.order,
    is_active: stage.is_active,
    template_override: stage.template_override ?? '',
    mode: stage.mode ?? '',
    allowed_tools: stage.allowed_tools ?? null,
    goal: stage.goal ?? '',
    is_sale: stage.is_sale ?? false,
    min_temperature: stage.min_temperature ?? '',
  };
}

function StageRow({
  stage,
  canManage,
}: {
  stage: FunnelStageConfig;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<StageDraft>(() => toDraft(stage));

  // Re-sync local draft when the underlying stage refetches.
  useEffect(() => {
    setDraft(toDraft(stage));
  }, [stage]);

  const mutation = useMutation({
    mutationFn: (payload: UpdateFunnelStagePayload) =>
      funnelApi.updateStage(stage.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funnel-stages'] });
      toast.success(`Стадия «${draft.label}» сохранена`);
    },
    onError: () => toast.error('Не удалось сохранить стадию'),
  });

  const isDirty =
    draft.label !== stage.label ||
    draft.order !== stage.order ||
    draft.is_active !== stage.is_active ||
    draft.template_override !== (stage.template_override ?? '') ||
    draft.mode !== (stage.mode ?? '') ||
    draft.goal !== (stage.goal ?? '') ||
    draft.is_sale !== (stage.is_sale ?? false) ||
    draft.min_temperature !== (stage.min_temperature ?? '') ||
    JSON.stringify(draft.allowed_tools) !==
      JSON.stringify(stage.allowed_tools ?? null);

  const handleSave = () => {
    mutation.mutate({
      label: draft.label,
      order: draft.order,
      is_active: draft.is_active,
      template_override: draft.template_override.trim() || null,
      mode: draft.mode,
      allowed_tools: draft.allowed_tools,
      goal: draft.goal,
      is_sale: draft.is_sale,
      min_temperature: draft.min_temperature,
    });
  };

  /** Whether "all tools" is on (allowed_tools === null). */
  const allToolsOn = draft.allowed_tools === null;

  const toggleAllTools = (on: boolean) => {
    setDraft((d) => ({
      ...d,
      allowed_tools: on ? null : [],
    }));
  };

  const toggleTool = (tool: string, checked: boolean) => {
    setDraft((d) => {
      const current = d.allowed_tools ?? [];
      const next = checked
        ? [...current, tool]
        : current.filter((t) => t !== tool);
      return { ...d, allowed_tools: next };
    });
  };

  return (
    <div
      data-testid={`stage-row-${stage.codename}`}
      className="rounded-md border px-3 py-3 space-y-3"
    >
      {/* ---- Primary row: label / order / active / save ---- */}
      <div className="grid grid-cols-12 items-end gap-3">
        <div className="col-span-12 sm:col-span-5 space-y-1">
          <Label htmlFor={`stage-label-${stage.id}`} className="text-xs text-gray-500">
            {stage.codename}
          </Label>
          <Input
            id={`stage-label-${stage.id}`}
            data-testid={`stage-label-${stage.codename}`}
            value={draft.label}
            disabled={!canManage}
            onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
          />
        </div>

        <div className="col-span-4 sm:col-span-2 space-y-1">
          <Label htmlFor={`stage-order-${stage.id}`} className="text-xs text-gray-500">
            Порядок
          </Label>
          <Input
            id={`stage-order-${stage.id}`}
            data-testid={`stage-order-${stage.codename}`}
            type="number"
            value={draft.order}
            disabled={!canManage}
            onChange={(e) =>
              setDraft((d) => ({ ...d, order: Number(e.target.value) }))
            }
          />
        </div>

        <div className="col-span-5 sm:col-span-3 flex items-center gap-2 pb-2">
          <Switch
            data-testid={`stage-active-${stage.codename}`}
            checked={draft.is_active}
            disabled={!canManage}
            onCheckedChange={(checked) =>
              setDraft((d) => ({ ...d, is_active: checked }))
            }
            aria-label={`Активна стадия ${draft.label}`}
          />
          <span className="text-sm text-gray-600">
            {draft.is_active ? 'Активна' : 'Скрыта'}
          </span>
        </div>

        {canManage && (
          <div className="col-span-3 sm:col-span-2 flex justify-end pb-1">
            <Button
              data-testid={`stage-save-${stage.codename}`}
              size="sm"
              variant="outline"
              disabled={!isDirty || mutation.isPending}
              onClick={handleSave}
            >
              {mutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
            </Button>
          </div>
        )}
      </div>

      {/* ---- Extended settings: mode / allowed_tools ---- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t pt-3">
        {/* Mode */}
        <div className="space-y-1">
          <Label className="text-xs text-gray-500">Режим</Label>
          <Select
            value={draft.mode || '_default'}
            disabled={!canManage}
            onValueChange={(v) =>
              setDraft((d) => ({ ...d, mode: (v === '_default' ? '' : v) as StageMode }))
            }
          >
            <SelectTrigger
              data-testid={`stage-mode-${stage.codename}`}
              className="w-full"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MODE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value || '_default'} value={opt.value || '_default'}>
                  <span title={opt.hint}>{opt.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-gray-400">
            {MODE_OPTIONS.find((o) => o.value === draft.mode)?.hint}
          </p>

          {/* KAN-269: is_sale flag */}
          <div className="flex items-center gap-2 pt-2">
            <Switch
              data-testid={`stage-is-sale-${stage.codename}`}
              checked={draft.is_sale}
              disabled={!canManage}
              onCheckedChange={(checked) =>
                setDraft((d) => ({ ...d, is_sale: checked }))
              }
              aria-label={`Продажа на стадии ${draft.label}`}
            />
            <span className="text-sm text-gray-600">Продажа</span>
          </div>
          <p className="text-[11px] text-gray-400">
            События, ведущие на этот этап, записываются как покупка клиента.
          </p>

          {/* KAN-966: lead temperature reached on this stage (funnel goal) */}
          <div className="space-y-1 pt-2">
            <Label className="text-xs text-gray-500">Теплота при достижении этапа</Label>
            <Select
              value={draft.min_temperature || NO_TEMPERATURE}
              disabled={!canManage}
              onValueChange={(v) =>
                setDraft((d) => ({
                  ...d,
                  min_temperature: (v === NO_TEMPERATURE ? '' : v) as StageMinTemperature,
                }))
              }
            >
              <SelectTrigger
                data-testid={`stage-min-temperature-${stage.codename}`}
                className="w-full"
                aria-label={`Теплота при достижении этапа ${draft.label}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TEMPERATURE}>Не менять</SelectItem>
                {LEAD_TEMPERATURE_LEVELS.map((level) => (
                  <SelectItem key={level} value={level}>
                    {LEAD_TEMPERATURE_LABELS[level]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-gray-400">
              Клиент на этом этапе получает теплоту не ниже выбранной. Ручную оценку не меняет.
            </p>
          </div>
        </div>

        {/* Allowed tools */}
        <div className="space-y-2">
          <Label className="text-xs text-gray-500">Инструменты</Label>
          <div className="flex items-center gap-2">
            <Switch
              data-testid={`stage-all-tools-${stage.codename}`}
              checked={allToolsOn}
              disabled={!canManage}
              onCheckedChange={toggleAllTools}
              aria-label="Все инструменты"
            />
            <span className="text-sm text-gray-600">
              {allToolsOn ? 'Все инструменты' : 'Выборочно'}
            </span>
          </div>
          <div className="space-y-1">
            {KNOWN_TOOLS.map((tool) => {
              const isChecked = allToolsOn || (draft.allowed_tools?.includes(tool.value) ?? false);
              return (
                <label
                  key={tool.value}
                  className="flex items-center gap-2 text-sm text-gray-600"
                >
                  <Checkbox
                    data-testid={`stage-tool-${tool.value}-${stage.codename}`}
                    checked={isChecked}
                    disabled={allToolsOn || !canManage}
                    onCheckedChange={(checked) => toggleTool(tool.value, checked)}
                    aria-label={tool.label}
                  />
                  {tool.label}
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* ---- Stage goal ({{funnel.goal}}) ---- */}
      <div className="space-y-1 border-t pt-3">
        <Label
          htmlFor={`stage-goal-${stage.id}`}
          className="text-xs text-gray-500"
        >
          Цель этапа
        </Label>
        <Textarea
          id={`stage-goal-${stage.id}`}
          data-testid={`stage-goal-${stage.codename}`}
          rows={2}
          value={draft.goal}
          disabled={!canManage}
          placeholder="Короткая цель этапа — подставляется в {{funnel.goal}}"
          onChange={(e) => setDraft((d) => ({ ...d, goal: e.target.value }))}
        />
        <p className="text-[11px] text-gray-400">
          Короткая цель этапа (маркер {'{{funnel.goal}}'}), отдельно от детального
          промпта.
        </p>
      </div>
    </div>
  );
}

/** Editable local copy of a next-step type row. */
interface NextStepTypeDraft {
  label: string;
  event_codename: string;
  is_active: boolean;
  order: number;
}

function NextStepTypeRow({
  type,
  canManage,
}: {
  type: NextStepType;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<NextStepTypeDraft>(() => ({
    label: type.label,
    event_codename: type.event_codename,
    is_active: type.is_active,
    order: type.order,
  }));

  useEffect(() => {
    setDraft({
      label: type.label,
      event_codename: type.event_codename,
      is_active: type.is_active,
      order: type.order,
    });
  }, [type]);

  const saveMutation = useMutation({
    mutationFn: () =>
      funnelApi.updateNextStepType(type.id, {
        label: draft.label,
        event_codename: draft.event_codename,
        is_active: draft.is_active,
        order: draft.order,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funnel-next-step-types'] });
      toast.success(`Тип «${draft.label}» сохранён`);
    },
    onError: () => toast.error('Не удалось сохранить тип'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => funnelApi.deleteNextStepType(type.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funnel-next-step-types'] });
      toast.success('Тип удалён');
    },
    onError: () => toast.error('Не удалось удалить тип'),
  });

  const isDirty =
    draft.label !== type.label ||
    draft.event_codename !== type.event_codename ||
    draft.is_active !== type.is_active ||
    draft.order !== type.order;

  return (
    <div
      data-testid={`next-step-type-row-${type.codename}`}
      className="rounded-md border px-3 py-3"
    >
      <div className="grid grid-cols-12 items-end gap-3">
        <div className="col-span-12 sm:col-span-4 space-y-1">
          <Label
            htmlFor={`nst-label-${type.id}`}
            className="text-xs text-gray-500"
          >
            {type.codename}
          </Label>
          <Input
            id={`nst-label-${type.id}`}
            data-testid={`next-step-type-label-${type.codename}`}
            value={draft.label}
            disabled={!canManage}
            onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
          />
        </div>

        <div className="col-span-8 sm:col-span-4 space-y-1">
          <Label
            htmlFor={`nst-event-${type.id}`}
            className="text-xs text-gray-500"
          >
            Событие
          </Label>
          <Input
            id={`nst-event-${type.id}`}
            data-testid={`next-step-type-event-${type.codename}`}
            value={draft.event_codename}
            disabled={!canManage}
            onChange={(e) =>
              setDraft((d) => ({ ...d, event_codename: e.target.value }))
            }
          />
        </div>

        <div className="col-span-4 sm:col-span-1 space-y-1">
          <Label
            htmlFor={`nst-order-${type.id}`}
            className="text-xs text-gray-500"
          >
            Порядок
          </Label>
          <Input
            id={`nst-order-${type.id}`}
            data-testid={`next-step-type-order-${type.codename}`}
            type="number"
            value={draft.order}
            disabled={!canManage}
            onChange={(e) =>
              setDraft((d) => ({ ...d, order: Number(e.target.value) }))
            }
          />
        </div>

        <div className="col-span-5 sm:col-span-2 flex items-center gap-2 pb-2">
          <Switch
            data-testid={`next-step-type-active-${type.codename}`}
            checked={draft.is_active}
            disabled={!canManage}
            onCheckedChange={(checked) =>
              setDraft((d) => ({ ...d, is_active: checked }))
            }
            aria-label={`Активен тип ${draft.label}`}
          />
          <span className="text-sm text-gray-600">
            {draft.is_active ? 'Вкл' : 'Выкл'}
          </span>
        </div>

        {canManage && (
          <div className="col-span-3 sm:col-span-1 flex justify-end gap-1 pb-1">
            <Button
              data-testid={`next-step-type-save-${type.codename}`}
              size="sm"
              variant="outline"
              disabled={!isDirty || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
            </Button>
            <Button
              data-testid={`next-step-type-delete-${type.codename}`}
              size="sm"
              variant="ghost"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
              aria-label={`Удалить тип ${draft.label}`}
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function NextStepTypeAddForm() {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  const [codename, setCodename] = useState('');
  const [eventCodename, setEventCodename] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      funnelApi.createNextStepType({
        label: label.trim(),
        codename: codename.trim(),
        event_codename: eventCodename.trim(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funnel-next-step-types'] });
      toast.success('Тип добавлен');
      setLabel('');
      setCodename('');
      setEventCodename('');
    },
    onError: () => toast.error('Не удалось добавить тип'),
  });

  const canSubmit =
    label.trim().length > 0 &&
    codename.trim().length > 0 &&
    eventCodename.trim().length > 0 &&
    !mutation.isPending;

  return (
    <div
      data-testid="next-step-type-add-form"
      className="grid grid-cols-12 items-end gap-3 rounded-md border border-dashed px-3 py-3"
    >
      <div className="col-span-12 sm:col-span-4 space-y-1">
        <Label htmlFor="nst-new-label" className="text-xs text-gray-500">
          Название
        </Label>
        <Input
          id="nst-new-label"
          data-testid="next-step-type-new-label"
          value={label}
          placeholder="Замер"
          onChange={(e) => setLabel(e.target.value)}
        />
      </div>
      <div className="col-span-6 sm:col-span-3 space-y-1">
        <Label htmlFor="nst-new-codename" className="text-xs text-gray-500">
          Код (латиница)
        </Label>
        <Input
          id="nst-new-codename"
          data-testid="next-step-type-new-codename"
          value={codename}
          placeholder="measurement"
          onChange={(e) => setCodename(e.target.value)}
        />
      </div>
      <div className="col-span-6 sm:col-span-3 space-y-1">
        <Label htmlFor="nst-new-event" className="text-xs text-gray-500">
          Событие
        </Label>
        <Input
          id="nst-new-event"
          data-testid="next-step-type-new-event"
          value={eventCodename}
          placeholder="measurement.scheduled"
          onChange={(e) => setEventCodename(e.target.value)}
        />
      </div>
      <div className="col-span-12 sm:col-span-2 flex justify-end pb-1">
        <Button
          data-testid="next-step-type-add-submit"
          size="sm"
          disabled={!canSubmit}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Plus className="h-4 w-4 mr-1" /> Добавить
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

function MappingRow({
  mapping,
  stages,
  canManage,
}: {
  mapping: EventStageMapping;
  stages: FunnelStageConfig[];
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: (target: string) =>
      funnelApi.updateMapping(mapping.id, { target_stage: target }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funnel-mappings'] });
      toast.success('Маппинг обновлён');
    },
    onError: () => toast.error('Не удалось обновить маппинг'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => funnelApi.deleteMapping(mapping.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funnel-mappings'] });
      toast.success('Маппинг удалён');
    },
    onError: () => toast.error('Не удалось удалить маппинг'),
  });

  return (
    <div
      data-testid={`mapping-row-${mapping.event_codename}`}
      className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
    >
      <span className="truncate text-sm text-gray-700">
        {mapping.event_label || mapping.event_codename}
      </span>
      <div className="flex items-center gap-2">
        <Select
          value={mapping.target_stage}
          disabled={!canManage}
          onValueChange={(v) => updateMutation.mutate(v)}
        >
          <SelectTrigger
            data-testid={`mapping-target-${mapping.event_codename}`}
            className="w-48"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {stages.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canManage && (
          <Button
            data-testid={`mapping-delete-${mapping.event_codename}`}
            size="sm"
            variant="ghost"
            disabled={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate()}
            aria-label={`Удалить маппинг ${mapping.event_codename}`}
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        )}
      </div>
    </div>
  );
}

export function FunnelConfig({ canManage = true }: { canManage?: boolean }) {
  const {
    data: stages,
    isLoading: stagesLoading,
    isError: stagesError,
  } = useQuery({
    queryKey: ['funnel-stages'],
    queryFn: funnelApi.listStages,
  });

  const {
    data: mappings,
    isLoading: mappingsLoading,
  } = useQuery({
    queryKey: ['funnel-mappings'],
    queryFn: funnelApi.listMappings,
  });

  const {
    data: nextStepTypes,
    isLoading: typesLoading,
  } = useQuery({
    queryKey: ['funnel-next-step-types'],
    queryFn: funnelApi.listNextStepTypes,
  });

  const sortedStages = [...(stages ?? [])].sort((a, b) => a.order - b.order);
  const sortedTypes = [...(nextStepTypes ?? [])].sort((a, b) => a.order - b.order);

  return (
    <div data-testid="funnel-config" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Стадии воронки</CardTitle>
        </CardHeader>
        <CardContent>
          {stagesLoading ? (
            <div
              data-testid="stages-config-loading"
              className="flex items-center justify-center py-12"
            >
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : stagesError ? (
            <p className="text-sm text-red-600">Ошибка загрузки стадий</p>
          ) : sortedStages.length === 0 ? (
            <p className="text-sm text-gray-500">Стадии не настроены</p>
          ) : (
            <div className="space-y-3">
              {sortedStages.map((stage) => (
                <StageRow key={stage.id} stage={stage} canManage={canManage} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Типы следующего шага</CardTitle>
        </CardHeader>
        <CardContent>
          {typesLoading ? (
            <div
              data-testid="next-step-types-loading"
              className="flex items-center justify-center py-12"
            >
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="space-y-3">
              {sortedTypes.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Типы следующего шага не настроены
                </p>
              ) : (
                sortedTypes.map((type) => (
                  <NextStepTypeRow
                    key={type.id}
                    type={type}
                    canManage={canManage}
                  />
                ))
              )}
              {canManage && <NextStepTypeAddForm />}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Исход → стадия</CardTitle>
        </CardHeader>
        <CardContent>
          {mappingsLoading ? (
            <div
              data-testid="mappings-config-loading"
              className="flex items-center justify-center py-12"
            >
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (mappings?.length ?? 0) === 0 ? (
            <p className="text-sm text-gray-500">Маппинги не настроены</p>
          ) : (
            <div className="space-y-2">
              {mappings!.map((mapping) => (
                <MappingRow
                  key={mapping.id}
                  mapping={mapping}
                  stages={sortedStages}
                  canManage={canManage}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default FunnelConfig;
